import { create } from 'zustand';
import { useSession } from '../../state/session.ts';
import { ApiError } from '../onboarding/api.ts';
import { discoveryClient } from './client.ts';
import { isAttemptActive } from './model.ts';
import type {
  DiscoveryEligibility,
  DiscoveryError,
  DiscoveryProgressResponse,
  DiscoveryRecords,
  DiscoveryStopResponse,
  Finding,
  StartDiscoveryRequest,
  StartDiscoveryResponse,
  WorkspaceCredentials,
} from './types.ts';

interface PendingStart {
  key: string;
  request: StartDiscoveryRequest;
}

const PENDING_START_PREFIX = 'traceira-discovery-start:';

function pendingStartStorageKey(workspaceId: string) {
  return `${PENDING_START_PREFIX}${workspaceId}`;
}

function parsePendingStart(value: unknown): PendingStart | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Partial<PendingStart>;
  const request = candidate.request as Partial<StartDiscoveryRequest> | undefined;
  if (
    typeof candidate.key !== 'string' ||
    !/^[\x21-\x7E]{16,200}$/.test(candidate.key) ||
    !request ||
    typeof request !== 'object' ||
    Object.keys(request).some((key) => key !== 'profileId' && key !== 'profileVersion') ||
    typeof request.profileId !== 'string' ||
    request.profileId.length === 0 ||
    request.profileId.length > 160 ||
    /\p{Cc}/u.test(request.profileId) ||
    !Number.isInteger(request.profileVersion) ||
    !request.profileVersion ||
    request.profileVersion < 1
  ) {
    return null;
  }
  return {
    key: candidate.key,
    request: { profileId: request.profileId, profileVersion: request.profileVersion },
  };
}

function readPendingStart(workspaceId: string): PendingStart | null {
  try {
    const raw = window.sessionStorage.getItem(pendingStartStorageKey(workspaceId));
    if (!raw) return null;
    const value: unknown = JSON.parse(raw);
    return parsePendingStart(value);
  } catch {
    return null;
  }
}

function savePendingStart(workspaceId: string, pending: PendingStart) {
  try {
    window.sessionStorage.setItem(pendingStartStorageKey(workspaceId), JSON.stringify(pending));
  } catch {
    // The Zustand store still retains the key for retries in this page session.
  }
}

function clearPendingStart(workspaceId: string, expectedKey?: string) {
  try {
    if (expectedKey) {
      const current = readPendingStart(workspaceId);
      if (current && current.key !== expectedKey) return;
    }
    window.sessionStorage.removeItem(pendingStartStorageKey(workspaceId));
  } catch {
    // A failed storage cleanup must not interrupt a server-confirmed response.
  }
}

function newIdempotencyKey() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `traceira-${crypto.randomUUID()}`;
    }
  } catch {
    // Fall through to a unique, non-secret browser-local command key.
  }
  return `traceira-${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

const OFFLINE = "We can't reach Traceira right now. The last confirmed discovery data is shown.";

function toDiscoveryError(error: unknown): DiscoveryError {
  if (!(error instanceof ApiError)) return { message: OFFLINE };
  const rawCode =
    error.body.code ??
    (typeof error.body.error === 'object' && error.body.error !== null
      ? (error.body.error as Record<string, unknown>).code
      : undefined);
  const code =
    typeof rawCode === 'string' && /^[a-z0-9_.-]{1,80}$/i.test(rawCode)
      ? rawCode
      : /^[a-z0-9_.-]{1,80}$/i.test(error.code)
        ? error.code
        : undefined;
  const rawRecovery =
    error.body.recoveryAction ??
    (typeof error.body.error === 'object' && error.body.error !== null
      ? (error.body.error as Record<string, unknown>).recoveryAction
      : undefined);
  const recoveryAction =
    typeof rawRecovery === 'string' && rawRecovery.length <= 1000 ? rawRecovery : undefined;

  let message = error.message;
  if (!message || message === 'error' || message === 'Error') {
    if (code === 'EXECUTION_DISABLED') {
      message = 'Discovery execution is disabled in this environment.';
    } else if (code === 'IDEMPOTENCY_PAYLOAD_MISMATCH') {
      message =
        'The saved command key belongs to a different request; review the current scope and retry only after confirming the latest attempt.';
    } else if (code === 'ACTIVE_RUN_CONFLICT') {
      message = 'An active discovery attempt already exists for this workspace.';
    } else if (code === 'NO_ACTIVE_ATTEMPT') {
      message = 'No active discovery attempt was found to stop.';
    } else if (code === 'REVOCATION_PENDING') {
      message = 'The workspace authorization mandate has been revoked.';
    } else if (code === 'STOP_REQUESTED') {
      message = 'Stop has already been requested for this discovery attempt.';
    } else if (code === 'STALE_CLAIM_FENCE') {
      message = 'The coordinator claim fence is stale.';
    }
  }

  return {
    message,
    ...(code ? { code } : {}),
    ...(recoveryAction ? { recoveryAction } : {}),
  };
}

function responseIsUncertain(error: unknown) {
  return (
    !(error instanceof ApiError) ||
    error.status === 0 ||
    error.status === 429 ||
    error.status >= 500
  );
}

function statusIsActive(status: string | undefined) {
  return status === 'QUEUED' || status === 'RUNNING' || status === 'STOP_REQUESTED';
}

export interface DiscoveryState {
  workspaceId: string | null;
  eligibility: DiscoveryEligibility | null;
  records: DiscoveryRecords | null;
  progress: DiscoveryProgressResponse | null;
  eligibilityLoading: boolean;
  recordsLoading: boolean;
  progressLoading: boolean;
  eligibilityLoaded: boolean;
  recordsLoaded: boolean;
  progressLoaded: boolean;
  eligibilityStale: boolean;
  recordsStale: boolean;
  progressStale: boolean;
  eligibilityError: DiscoveryError | null;
  recordsError: DiscoveryError | null;
  progressError: DiscoveryError | null;
  pendingStart: PendingStart | null;
  startResponse: StartDiscoveryResponse | null;
  startPending: boolean;
  startRetryAvailable: boolean;
  startError: DiscoveryError | null;
  stopPending: boolean;
  stopAcknowledgement: DiscoveryStopResponse | null;
  stopError: DiscoveryError | null;
  loadEligibility: (workspaceId: string, credentials?: WorkspaceCredentials) => Promise<void>;
  loadLatestAttempt: (workspaceId: string, credentials?: WorkspaceCredentials) => Promise<void>;
  loadProgress: (workspaceId: string, credentials?: WorkspaceCredentials) => Promise<void>;
  load: (workspaceId: string, credentials?: WorkspaceCredentials) => Promise<void>;
  startDiscovery: (
    workspaceId: string,
    input: StartDiscoveryRequest,
    credentials?: WorkspaceCredentials,
  ) => Promise<void>;
  stopDiscovery: (workspaceId: string, credentials?: WorkspaceCredentials) => Promise<void>;
  reset: () => void;
}

const empty = {
  workspaceId: null,
  eligibility: null,
  records: null,
  progress: null,
  eligibilityLoading: false,
  recordsLoading: false,
  progressLoading: false,
  eligibilityLoaded: false,
  recordsLoaded: false,
  progressLoaded: false,
  eligibilityStale: false,
  recordsStale: false,
  progressStale: false,
  eligibilityError: null,
  recordsError: null,
  progressError: null,
  pendingStart: null,
  startResponse: null,
  startPending: false,
  startRetryAvailable: false,
  startError: null,
  stopPending: false,
  stopAcknowledgement: null,
  stopError: null,
} satisfies Pick<
  DiscoveryState,
  | 'workspaceId'
  | 'eligibility'
  | 'records'
  | 'progress'
  | 'eligibilityLoading'
  | 'recordsLoading'
  | 'progressLoading'
  | 'eligibilityLoaded'
  | 'recordsLoaded'
  | 'progressLoaded'
  | 'eligibilityStale'
  | 'recordsStale'
  | 'progressStale'
  | 'eligibilityError'
  | 'recordsError'
  | 'progressError'
  | 'pendingStart'
  | 'startResponse'
  | 'startPending'
  | 'startRetryAvailable'
  | 'startError'
  | 'stopPending'
  | 'stopAcknowledgement'
  | 'stopError'
>;

/** 500 rows a page: a poll catches up by at most this many pages and resumes on the next one. */
const MAX_EVIDENCE_PAGES_PER_POLL = 10;

export const useDiscovery = create<DiscoveryState>()((set, get) => {
  let workspaceRevision = 0;
  let eligibilityRequest = 0;
  let recordsRequest = 0;
  let progressRequest = 0;
  // Where the live evidence pages stopped; empty until a live poll of this attempt has paged.
  let evidenceCursor = { attemptId: '', seq: 0 };

  const beginWorkspace = (workspaceId: string) => {
    if (get().workspaceId !== workspaceId) {
      workspaceRevision += 1;
      eligibilityRequest += 1;
      recordsRequest += 1;
      progressRequest += 1;
      evidenceCursor = { attemptId: '', seq: 0 };
      set({ ...empty, workspaceId, pendingStart: readPendingStart(workspaceId) });
    }
    return workspaceRevision;
  };

  /**
   * While a run is active, reads the attempt without evidence plus only the evidence rows that
   * arrived since the last poll, so a large scan is not resent every few seconds. Any other
   * read, including the first one after the run ends, is a full read.
   */
  const readLatest = async (
    workspaceId: string,
    credentials: WorkspaceCredentials,
    previous: DiscoveryRecords | null,
  ) => {
    const none = { attemptId: '', seq: 0 };
    if (!previous || !isAttemptActive(previous.attempt)) {
      return {
        records: await discoveryClient.latestAttempt(workspaceId, credentials),
        cursor: none,
      };
    }
    const light = await discoveryClient.latestAttempt(workspaceId, credentials, true);
    if (!light || !isAttemptActive(light.attempt)) {
      return {
        records: await discoveryClient.latestAttempt(workspaceId, credentials),
        cursor: none,
      };
    }
    const { attemptId } = light.attempt;
    const resume = evidenceCursor.attemptId === attemptId;
    let evidence = resume ? previous.evidence : [];
    let seq = resume ? evidenceCursor.seq : 0;
    for (let pages = 0; pages < MAX_EVIDENCE_PAGES_PER_POLL; pages += 1) {
      const page = await discoveryClient.evidenceSince(workspaceId, attemptId, seq, credentials);
      evidence = [...evidence, ...page.evidence];
      seq = page.cursor;
      if (!page.more) break;
    }
    return { records: { ...light, evidence }, cursor: { attemptId, seq } };
  };

  return {
    ...empty,

    loadEligibility: async (workspaceId, credentials = {}) => {
      const revision = beginWorkspace(workspaceId);
      const request = ++eligibilityRequest;
      const current = () => revision === workspaceRevision && request === eligibilityRequest;
      set({ eligibilityLoading: true, eligibilityError: null });
      try {
        const eligibility = await discoveryClient.eligibility(workspaceId, credentials);
        if (!current()) return;
        set({
          eligibility,
          eligibilityLoaded: true,
          eligibilityLoading: false,
          eligibilityStale: false,
          eligibilityError: null,
        });
      } catch (error) {
        if (!current()) return;
        set((state) => ({
          eligibilityLoading: false,
          eligibilityStale: state.eligibilityLoaded,
          eligibilityError: toDiscoveryError(error),
        }));
      }
    },

    loadLatestAttempt: async (workspaceId, credentials = {}) => {
      const revision = beginWorkspace(workspaceId);
      const request = ++recordsRequest;
      const current = () => revision === workspaceRevision && request === recordsRequest;
      set({ recordsLoading: true, recordsError: null });
      try {
        const records = await readLatest(workspaceId, credentials, get().records);
        if (!current()) return;
        evidenceCursor = records.cursor;
        set({
          records: records.records,
          recordsLoaded: true,
          recordsLoading: false,
          recordsStale: false,
          recordsError: null,
        });
        const pending = get().pendingStart;
        if (pending && records.records?.attempt.start.idempotencyKey === pending.key) {
          clearPendingStart(workspaceId, pending.key);
          set({
            pendingStart: null,
            startPending: false,
            startRetryAvailable: false,
            startError: null,
          });
        }
      } catch (error) {
        if (!current()) return;
        set((state) => ({
          recordsLoading: false,
          recordsStale: state.recordsLoaded,
          recordsError: toDiscoveryError(error),
        }));
      }
    },

    loadProgress: async (workspaceId, credentials = {}) => {
      const revision = beginWorkspace(workspaceId);
      const request = ++progressRequest;
      const current = () =>
        revision === workspaceRevision &&
        request === progressRequest &&
        get().workspaceId === workspaceId;
      set({ progressLoading: true, progressError: null });
      try {
        const progress = await discoveryClient.progress(workspaceId, credentials);
        if (!current()) return;
        set({
          progress,
          progressLoaded: true,
          progressLoading: false,
          progressStale: false,
          progressError: null,
        });
      } catch (error) {
        if (!current()) return;
        if (error instanceof ApiError && error.status === 404) {
          set({
            progress: null,
            progressLoaded: true,
            progressLoading: false,
            progressStale: false,
            progressError: null,
          });
          return;
        }
        set((state) => ({
          progressLoading: false,
          progressStale: state.progressLoaded,
          progressError: toDiscoveryError(error),
        }));
      }
    },

    load: async (workspaceId, credentials = {}) => {
      await Promise.all([
        get().loadEligibility(workspaceId, credentials),
        get().loadLatestAttempt(workspaceId, credentials),
      ]);
      if (get().workspaceId !== workspaceId) return;
      if (get().records?.attempt) {
        await get().loadProgress(workspaceId, credentials);
        return;
      }
      progressRequest += 1;
      set({
        progress: null,
        progressLoaded: true,
        progressLoading: false,
        progressStale: false,
        progressError: null,
      });
    },

    startDiscovery: async (workspaceId, input, credentials = {}) => {
      const revision = beginWorkspace(workspaceId);
      const current = () => revision === workspaceRevision && get().workspaceId === workspaceId;
      const state = get();
      if (
        state.startPending ||
        state.eligibilityStale ||
        !state.recordsLoaded ||
        state.recordsStale ||
        Boolean(state.recordsError) ||
        !state.eligibility?.eligible ||
        !state.eligibility.executionAllowed ||
        isAttemptActive(state.records?.attempt ?? null) ||
        statusIsActive(state.records?.attempt.status ?? state.startResponse?.status)
      ) {
        return;
      }

      const pending = state.pendingStart ??
        readPendingStart(workspaceId) ?? {
          key: newIdempotencyKey(),
          request: input,
        };
      savePendingStart(workspaceId, pending);
      set({
        pendingStart: pending,
        startPending: true,
        startRetryAvailable: false,
        startError: null,
        startResponse: null,
        stopAcknowledgement: null,
        stopError: null,
      });

      try {
        const response = await discoveryClient.start(
          workspaceId,
          pending.request,
          pending.key,
          credentials,
        );
        clearPendingStart(workspaceId, pending.key);
        if (!current()) return;
        set({
          pendingStart: null,
          startResponse: response,
          startPending: false,
          startRetryAvailable: false,
          startError: null,
        });
        await Promise.all([
          get().loadLatestAttempt(workspaceId, credentials),
          get().loadProgress(workspaceId, credentials),
        ]);
      } catch (error) {
        if (!current()) return;
        const uncertain = responseIsUncertain(error);
        if (!uncertain) clearPendingStart(workspaceId, pending.key);
        set({
          pendingStart: uncertain ? pending : null,
          startPending: false,
          startRetryAvailable: uncertain,
          startError: toDiscoveryError(error),
        });
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          await Promise.all([
            get().loadEligibility(workspaceId, credentials),
            get().loadLatestAttempt(workspaceId, credentials),
            get().loadProgress(workspaceId, credentials),
          ]);
        }
      }
    },

    stopDiscovery: async (workspaceId, credentials = {}) => {
      const revision = beginWorkspace(workspaceId);
      const current = () => revision === workspaceRevision && get().workspaceId === workspaceId;
      const state = get();
      const attemptStatus = state.records?.attempt.status ?? state.startResponse?.status;
      if (
        state.stopPending ||
        (!isAttemptActive(state.records?.attempt ?? null) &&
          attemptStatus !== 'QUEUED' &&
          attemptStatus !== 'RUNNING' &&
          attemptStatus !== 'STOP_REQUESTED')
      ) {
        return;
      }

      set({ stopPending: true, stopError: null });
      try {
        const acknowledgement = await discoveryClient.stop(workspaceId, credentials);
        if (!current()) return;
        set({ stopPending: false, stopAcknowledgement: acknowledgement, stopError: null });
        await Promise.all([
          get().loadLatestAttempt(workspaceId, credentials),
          get().loadProgress(workspaceId, credentials),
        ]);
      } catch (error) {
        if (!current()) return;
        set({ stopPending: false, stopError: toDiscoveryError(error) });
        await Promise.all([
          get().loadLatestAttempt(workspaceId, credentials),
          get().loadProgress(workspaceId, credentials),
        ]);
      }
    },

    reset: () => {
      workspaceRevision += 1;
      eligibilityRequest += 1;
      recordsRequest += 1;
      progressRequest += 1;
      set({ ...empty });
    },
  };
});

export function getDnsRecordCount(records: DiscoveryRecords | null): number {
  if (!records?.evidence) return 0;
  return records.evidence.filter((e) => e.kind === 'DNS_RECORD').length;
}

export function getSourceFreshness(records: DiscoveryRecords | null): string | null {
  const first = records?.sourceRecords?.[0];
  if (!first) return null;
  let latest = first.capturedAt;
  for (const s of records?.sourceRecords ?? []) {
    if (s.capturedAt > latest) latest = s.capturedAt;
  }
  return latest;
}

export function getCoverageSummary(
  records: DiscoveryRecords | null,
  progress: DiscoveryProgressResponse | null,
): { totalUnits: number; completedUnits: number; pendingUnits: number } {
  if (progress?.summary) {
    return {
      totalUnits: progress.summary.totalUnits,
      completedUnits:
        progress.summary.completedUnits +
        progress.summary.failedUnits +
        progress.summary.partialUnits +
        progress.summary.skippedUnits,
      pendingUnits: progress.summary.runningUnits + progress.summary.notAttemptedUnits,
    };
  }
  if (!records?.attempt?.coverage) {
    return { totalUnits: 0, completedUnits: 0, pendingUnits: 0 };
  }
  const totalUnits = records.attempt.coverage.length;
  const completedUnits = records.attempt.coverage.filter(
    (c) =>
      c.status === 'COMPLETE' ||
      c.status === 'FAILED' ||
      c.status === 'PARTIAL' ||
      c.status === 'SKIPPED',
  ).length;
  const pendingUnits = records.attempt.coverage.filter(
    (c) => c.status === 'NOT_ATTEMPTED' || c.status === 'RUNNING',
  ).length;
  return { totalUnits, completedUnits, pendingUnits };
}

export function getMailPolicyFindings(records: DiscoveryRecords | null): Finding[] {
  if (!records?.findings) return [];
  return records.findings.filter(
    (f) =>
      f.title.toLowerCase().includes('spf') ||
      f.title.toLowerCase().includes('dmarc') ||
      f.description.toLowerCase().includes('spf') ||
      f.description.toLowerCase().includes('dmarc'),
  );
}

// Another workspace, or none after sign-out, never sees this one's records, even on pages that
// don't load them (search, the running badge).
useSession.subscribe((state, previous) => {
  if (state.workspace?.id !== previous.workspace?.id) useDiscovery.getState().reset();
});
